/**
 * Company logo resolution — settings upload, Qawaem default, print-safe absolute URLs.
 */
import { QAWAEM_LOGO } from '../../products/saudi/config/qawaemAssets';

export function getDefaultCompanyLogoUrl() {
  return QAWAEM_LOGO || '/qawaem/logo.png';
}

/** Logo shown in UI/PDF when settings.company.logo is empty. */
export function getEffectiveCompanyLogo(logo) {
  const trimmed = logo != null ? String(logo).trim() : '';
  return trimmed || getDefaultCompanyLogoUrl();
}

/**
 * Resolve logo for <img src> — data URLs as-is, relative paths → absolute for print windows.
 * @param {string|null|undefined} logo
 * @param {string} [origin] — e.g. window.location.origin
 */
export function resolveCompanyLogoSrc(logo, origin) {
  const raw = getEffectiveCompanyLogo(logo);
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return base ? `${base.replace(/\/$/, '')}${path}` : path;
}

/** Fetch image URL and return base64 data URL (for print popup / PDF). */
export async function embedImageAsDataUrl(src) {
  const url = String(src || '').trim();
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : url);
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

/** Load Qawaem default logo as data URL for persisting in settings.company.logo */
export async function loadDefaultCompanyLogoDataUrl(origin) {
  return embedImageAsDataUrl(resolveCompanyLogoSrc(null, origin));
}

/** Rewrite <img src="..."> in HTML for print (absolute or embedded data URLs). */
export function absolutizeImgSrcInHtml(html, origin, embeddedLogoDataUrl) {
  if (!html) return html;
  return html.replace(/(<img\b[^>]*\ssrc=")([^"]*)(")/gi, (match, prefix, src, suffix) => {
    if (!src || src.startsWith('data:')) return match;
    const next = embeddedLogoDataUrl && /logo|Logo|qawaem|ERPmarron|brand-protected/i.test(match)
      ? embeddedLogoDataUrl
      : resolveCompanyLogoSrc(src, origin);
    return `${prefix}${next}${suffix}`;
  });
}
